import { useCallback, useEffect, useState } from 'react';
import { pb } from './pb';

/*
 * Web Push for price alerts. Every device switches it on for itself; the
 * server keeps one subscription per device and sends to all of a user's.
 */

/**
 * on/off: this device can get notifications; ios-install: an iPhone or iPad
 * in Safari, where only apps on the home screen get them; denied: blocked in
 * the browser settings.
 */
export type PushState = 'loading' | 'on' | 'off' | 'denied' | 'ios-install' | 'unsupported';

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function pushState(): Promise<PushState> {
  // Safari on iOS only offers push to apps added to the home screen
  if (!supported()) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'off';
  return (await currentSubscription()) ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const own = sub.options.applicationServerKey;
  if (!own) return false;
  const a = new Uint8Array(own);
  return a.length === key.length && a.every((b, i) => b === key[i]);
}

export class PushDenied extends Error {}

/** Asks for permission (call it straight from a tap) and registers this device. */
export async function enablePush(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushDenied(permission);
  const reg = await navigator.serviceWorker.ready;
  const { public_key } = await pb.send<{ public_key: string }>('/api/closetly/push/key', {});
  const key = keyBytes(public_key);
  let sub = await reg.pushManager.getSubscription();
  // made for other server keys (e.g. a fresh server): start over
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await pb.send('/api/closetly/push/subscribe', { method: 'POST', body: sub.toJSON() });
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await forgetDevice(sub);
  await sub.unsubscribe();
}

function forgetDevice(sub: PushSubscription) {
  return pb.send('/api/closetly/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } });
}

/**
 * Before signing out: the account's alerts stop coming to this device. The
 * browser keeps its subscription for whoever signs in next.
 */
export async function forgetPushOnLogout(): Promise<void> {
  if (!supported()) return;
  const sub = await currentSubscription().catch(() => null);
  if (sub) await forgetDevice(sub).catch(() => {});
}

/** On start: tell the server about this device again, in case it lost it or another account used it. */
export async function resyncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (sub) await pb.send('/api/closetly/push/subscribe', { method: 'POST', body: sub.toJSON() });
}

export function sendTestPush() {
  return pb.send<{ sent: number }>('/api/closetly/push/test', { method: 'POST' });
}

export function usePushState() {
  const [state, setState] = useState<PushState>('loading');
  const refresh = useCallback(() => {
    pushState()
      .then(setState)
      .catch(() => setState('unsupported'));
  }, []);
  useEffect(refresh, [refresh]);
  return [state, refresh] as const;
}
